import {Component, computed, input, output} from '@angular/core';
import {RouterLink} from '@angular/router';
import {PublicStats} from '../services/stats.service';
import {CountUpComponent} from './count-up.component';
import {roundedParts} from './stat-parts';

/**
 * The home page's first screen: what Ludoya is, the way in, and the product itself on a phone
 * with two live figures beside it. Its own component so the home page stays within the style
 * budget and each part of it can be read on its own.
 */
@Component({
    selector: 'cb-home-hero',
    imports: [RouterLink, CountUpComponent],
    template: `
        <!-- ============================ HERO ============================
             Deliberately shorter than the viewport: a hero that fills the screen reads as the
             whole page (NN/g's "false floor"), and the events peeking in below are what tell the
             reader there is more. No stock photography: the product and live numbers are the
             picture. -->
        <section class="hero">
            <div class="wrap hero-grid">
                <div class="hero-copy">
                    <h1 class="hero-title" i18n="@@home-hero-title">Your next <span class="scribble">game night</span> starts here.</h1>
                    <p class="hero-sub" i18n="@@home-hero-sub">Log your plays, manage your collection and run game nights and tournaments. Find players and events near you too.</p>

                    <div class="hero-actions">
                        <button class="cb-cta" type="button" (click)="join.emit()">
                            <span i18n="@@start-free">Start free</span>
                            <span class="cb-cta-chevron" aria-hidden="true"><svg viewBox="0 0 24 24" width="18" height="18"><path d="M5 12h13M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
                        </button>
                        <a class="learn-link" [routerLink]="organizerLink()" i18n="@@home-hero-organizers">Running a club or event?</a>
                    </div>

                    <div class="hero-alt">
                        <span class="hero-alt-label" i18n="@@game-page-ludoya-eyebrow">Free, on web, Android and iOS</span>
                        <span class="store-badges">
                            <a class="store-badge" href="https://play.google.com/store/apps/details?id=com.ludoya.app&pcampaignid=pcampaignidMKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1"
                               target="_blank" rel="noopener noreferrer">
                                <img alt="Get it on Google Play" src="/assets/google-play-badge.png" width="270" height="80"/>
                            </a>
                            <a class="store-badge" href="https://apps.apple.com/us/app/ludoya/id6677054406" target="_blank" rel="noopener noreferrer">
                                <img alt="Download on the App Store" src="/assets/app-store-badge.svg" width="180" height="60"/>
                            </a>
                        </span>
                    </div>
                </div>

                <div class="hero-visual">
                    <p class="note note-hero" aria-hidden="true">
                        <span i18n="@@home-note-pocket">Your whole board game life, in your pocket</span>
                        <svg class="note-arrow" viewBox="0 0 80 60" width="72" height="54">
                            <path d="M70 6 C 58 30, 40 44, 12 46" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
                            <path d="M22 36 L 11 46 L 24 54" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </p>
                    <div class="phone">
                        <img class="phone-screen" src="/assets/screenshots/home-phone-profile.webp" width="1179" height="2334" fetchpriority="high"
                             alt="A Ludoya profile: recent plays, monthly stats, communities and upcoming events" i18n-alt="@@home-phone-alt"/>
                        <img class="phone-frame" src="/assets/phone-mockup.png" width="486" height="918" alt=""/>
                    </div>
                    @if (stats(); as s) {
                        <div class="chip chip-today">
                            <span class="live-dot" aria-hidden="true"></span>
                            <span class="chip-figure"><cb-count-up [value]="s.activeToday"/></span>
                            <span i18n="@@stat-label-active-today">Here today</span>
                        </div>
                        <div class="chip chip-plays">
                            <span class="chip-figure"><cb-count-up [value]="plays().n" [decimals]="plays().decimals" [suffix]="plays().suffix"/></span>
                            <span i18n="@@home-chip-plays">plays logged</span>
                        </div>
                    }
                </div>
            </div>
        </section>
    `,
    styles: [`
        :host {
            display: block;
            font-family: var(--h-body);
        }

        .wrap {
            max-width: 1168px;
            margin: 0 auto;
            padding: 0 24px;
        }

        /* Handwriting in the margins: the one playful voice on the page, always a little askew. */
        .note {
            margin: 0;
            font-family: var(--h-hand);
            font-weight: 600;
            line-height: 1.05;
            pointer-events: none;
        }

        /* ============================ HERO ============================ */
        .hero {
            overflow: hidden;
            color: white;
            padding: clamp(40px, 6vh, 72px) 0 clamp(72px, 9vh, 104px);
            background:
                radial-gradient(50% 60% at 88% 18%, rgba(48, 224, 192, 0.15) 0%, transparent 70%),
                radial-gradient(38% 45% at 6% 68%, rgba(224, 48, 112, 0.13) 0%, transparent 70%),
                var(--h-night);
        }

        .hero-grid {
            display: grid;
            grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr);
            gap: 56px;
            align-items: center;
        }

        .hero-title {
            margin: 0 0 22px;
            font-family: var(--h-display);
            font-size: clamp(2.8rem, 1.6rem + 4vw, 5.1rem);
            font-weight: 800;
            line-height: 1;
            letter-spacing: -0.04em;
            text-wrap: balance;
        }

        /* A raspberry stroke drawn under the words by hand, not a colour change on them. */
        .scribble {
            position: relative;
            white-space: nowrap;
        }

        .scribble::after {
            content: '';
            position: absolute;
            left: -2%;
            right: -2%;
            bottom: -0.12em;
            height: 0.22em;
            background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 20' preserveAspectRatio='none'%3E%3Cpath d='M4 13 C 50 5, 100 4, 140 9 S 210 16, 236 6' fill='none' stroke='%23E03070' stroke-width='6' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat center / 100% 100%;
        }

        .hero-sub {
            margin: 0 0 30px;
            max-width: 32em;
            font-size: clamp(1.05rem, 1rem + 0.3vw, 1.2rem);
            line-height: 1.6;
            color: rgba(255, 255, 255, 0.75);
        }

        .hero-actions {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 16px 28px;
        }

        .learn-link {
            font-weight: 600;
            color: rgba(255, 255, 255, 0.85);
            text-decoration: underline;
            text-decoration-color: rgba(255, 255, 255, 0.3);
            text-underline-offset: 5px;
            transition: color 0.2s ease, text-decoration-color 0.2s ease;
        }

        .learn-link:hover {
            color: white;
            text-decoration-color: white;
        }

        .learn-link:focus-visible {
            outline: 2px solid var(--h-sun);
            outline-offset: 4px;
            border-radius: 4px;
        }

        .hero-alt {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 12px 16px;
            margin-top: 34px;
            font-size: 0.9rem;
            color: rgba(255, 255, 255, 0.6);
        }

        .store-badges {
            display: inline-flex;
            gap: 8px;
        }

        .store-badge {
            display: block;
            border-radius: 7px;
            transition: opacity 0.2s ease;
        }

        .store-badge:hover {
            opacity: 0.85;
        }

        .store-badge:focus-visible {
            outline: 2px solid var(--h-sun);
            outline-offset: 3px;
        }

        .store-badge img {
            display: block;
            height: 36px;
            width: auto;
        }

        .hero-visual {
            position: relative;
            justify-self: center;
            width: min(100%, 320px);
            margin-right: 40px;
        }

        /* The site's own phone frame (486x918, a transparent 393x778 screen at 48,62) over a capture
           taken at exactly that screen size, so nothing is cropped or stretched. */
        .phone {
            position: relative;
            aspect-ratio: 486 / 918;
            transform: rotate(3deg);
            filter: drop-shadow(0 40px 50px rgba(0, 0, 0, 0.55));
        }

        .phone-screen {
            position: absolute;
            left: 9.877%;
            top: 6.754%;
            width: 80.864%;
            height: 84.749%;
            object-fit: cover;
            object-position: top;
            background: #0E1B1A;
        }

        .phone-frame {
            position: relative;
            display: block;
            width: 100%;
            height: auto;
        }

        .chip {
            position: absolute;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 10px 16px;
            border-radius: 999px;
            background: white;
            color: var(--h-ink);
            font-size: 0.9rem;
            font-weight: 600;
            white-space: nowrap;
            box-shadow: 0 18px 36px -16px rgba(0, 0, 0, 0.7);
        }

        .chip-figure {
            font-family: var(--h-display);
            font-size: 1.1rem;
            font-weight: 800;
        }

        .chip-today {
            top: 49%;
            left: -104px;
            transform: rotate(-3deg);
        }

        .chip-plays {
            bottom: 3%;
            right: -60px;
            transform: rotate(2deg);
        }

        .live-dot {
            width: 9px;
            height: 9px;
            border-radius: 50%;
            background: var(--h-mint);
            animation: live 2.4s ease-out infinite;
        }

        @keyframes live {
            0% { box-shadow: 0 0 0 0 rgba(48, 224, 192, 0.55); }
            70%, 100% { box-shadow: 0 0 0 9px rgba(48, 224, 192, 0); }
        }

        /* Anchored just past the phone's right edge and wrapped to a fixed width, so a longer
           translation grows downwards instead of into the phone. */
        .note-hero {
            position: absolute;
            z-index: 1;
            top: 40px;
            left: calc(100% + 14px);
            width: 190px;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
            font-size: 1.7rem;
            color: var(--h-sun);
            transform: rotate(-6deg);
        }

        .note-arrow {
            color: currentColor;
        }

        /* Two columns but no room beside the phone: the note hangs under it, pointing up, rather
           than above it where the header would cut it off. */
        @media (min-width: 961px) and (max-width: 1320px) {
            .hero-visual {
                margin-bottom: 72px;
            }

            .note-hero {
                top: calc(100% + 4px);
                left: auto;
                right: -24px;
                width: 240px;
                flex-direction: column-reverse;
                align-items: flex-end;
                transform: rotate(-4deg);
            }

            .note-hero .note-arrow {
                transform: scaleY(-1);
            }
        }

        @media (max-width: 960px) {
            .note-hero {
                left: auto;
                right: -60px;
                top: -64px;
            }
        }

        @media (max-width: 960px) {
            .hero-grid {
                grid-template-columns: minmax(0, 1fr);
            }

            .hero-visual {
                margin: 24px auto 0;
                width: min(78%, 300px);
            }

            .chip-today {
                left: -28px;
            }

            .chip-plays {
                right: -40px;
            }
        }

        @media (max-width: 600px) {
            .wrap {
                padding: 0 16px;
            }

            .note-hero {
                font-size: 1.35rem;
                right: -28px;
                top: -30px;
            }

            .note-hero .note-arrow {
                width: 52px;
            }
        }

        @media (prefers-reduced-motion: reduce) {
            .live-dot { animation: none; }
        }
    `],
})
export class HomeHeroComponent {
    readonly stats = input<PublicStats | null>(null);
    readonly organizerLink = input.required<string>();
    readonly join = output<void>();

    readonly plays = computed(() => roundedParts(this.stats()?.playsLogged ?? 0));
}
